import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { sendVerificationCode, checkVerificationCode } from '../api';

// Bundled with the app, not pulled from Webflow's CDN. The company is off Webflow;
// an external host we no longer pay for is one lapsed invoice away from a
// broken logo on a customer-facing page.
const LOGO_URL = '/hts-logo-white.png';

const INSURANCE_REQUIREMENTS = [
  { coverage: 'General Liability \u2014 Each Occurrence', limit: '$1,000,000' },
  { coverage: 'General Liability \u2014 Personal & Advertising Injury', limit: '$1,000,000' },
  { coverage: 'General Liability \u2014 Products/Completed Ops Aggregate', limit: '$2,000,000' },
  { coverage: 'General Liability \u2014 General Aggregate', limit: '$2,000,000' },
  { coverage: 'Automobile Liability \u2014 Combined Single Limit', limit: '$1,000,000' },
  { coverage: 'Automobile Liability \u2014 Contractual Liability', limit: '$1,000,000' },
  { coverage: 'Workers\u2019 Compensation', limit: 'As required by law' },
  { coverage: 'Employer\u2019s Liability \u2014 Each Accident', limit: '$500,000' },
];

export function LandingPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const handleSendCode = useCallback(async () => {
    if (!email.trim() || sending) return;
    setSending(true);
    setError(null);

    try {
      await sendVerificationCode(email.trim());
      setCodeSent(true);
      setResendCountdown(60);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send verification code';
      setError(message);
    } finally {
      setSending(false);
    }
  }, [email, sending]);

  const handleVerify = useCallback(async () => {
    if (!code.trim() || verifying) return;
    setVerifying(true);
    setError(null);

    try {
      // MUL-3919 §3.1: /verify/check now returns { verified, paymentTermsNegotiable }.
      // The wizard needs the boolean to decide whether to render the
      // Agreement Terms step. Persist it in sessionStorage alongside the
      // verified email; OnboardingForm also reads it on mount.
      const { paymentTermsNegotiable } = await checkVerificationCode(email.trim(), code.trim());
      sessionStorage.setItem('hts_verified_email', email.trim().toLowerCase());
      sessionStorage.setItem(
        'hts_payment_terms_negotiable',
        paymentTermsNegotiable ? 'true' : 'false',
      );
      navigate('/apply');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      setError(message);
    } finally {
      setVerifying(false);
    }
  }, [email, code, verifying, navigate]);

  const handleResend = useCallback(async () => {
    if (resendCountdown > 0 || sending) return;
    setSending(true);
    setError(null);
    setCode('');

    try {
      await sendVerificationCode(email.trim());
      setResendCountdown(60);
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setSending(false);
    }
  }, [email, resendCountdown, sending]);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <div className="app-container">
      <header className="app-header">
        <img src={LOGO_URL} alt="Harris Technology Services" />
        <h1>Subcontractor Onboarding</h1>
      </header>

      <main className="app-main" style={{ maxWidth: 800 }}>
        {/* Hero */}
        <div className="card landing-hero">
          <h2>Apply to Work With HTS</h2>
          <p>
            Thank you for your interest in partnering with Harris Technology Services.
            This application collects the information we need to get you set up as a subcontractor so we can start sending you work.
          </p>
          <p style={{ marginTop: 8, fontSize: 14, color: '#6B7280' }}>
            The form has 8 short steps and takes about 10&ndash;15 minutes to fill out.
          </p>
        </div>

        {/* What You'll Need */}
        <div className="card">
          <h3 className="section-title">Gather These Items Before You Start</h3>
          <p className="section-subtitle">Having these ready will help you finish the form faster:</p>
          <ul className="checklist">
            <li>
              <span className="check-icon">1</span>
              <span><strong>Your company name and address</strong> &mdash; the legal name your company does business under</span>
            </li>
            <li>
              <span className="check-icon">2</span>
              <span><strong>Your EIN (Tax ID number)</strong> &mdash; this is a 9-digit number from the IRS, found on your W-9 or IRS letters</span>
            </li>
            <li>
              <span className="check-icon">3</span>
              <span><strong>Certificate of Insurance (COI)</strong> &mdash; a document from your insurance company showing your coverage. Ask your insurance agent if you need a copy.</span>
            </li>
            <li>
              <span className="check-icon">4</span>
              <span><strong>A completed W-9 form</strong> &mdash; signed and dated this year. <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hts-action-blue)' }}>Download a blank W-9 here</a>.</span>
            </li>
            <li>
              <span className="check-icon">5</span>
              <span><strong>Your bank account information</strong> &mdash; bank name, routing number, and account number (found on the bottom of your checks)</span>
            </li>
            <li>
              <span className="check-icon">6</span>
              <span><strong>A voided check or bank letter</strong> &mdash; a photo or scan of a voided check, or a letter from your bank confirming your account</span>
            </li>
          </ul>
        </div>

        {/* Insurance Requirements */}
        <div className="card">
          <h3 className="section-title">Insurance Requirements</h3>
          <p className="section-subtitle">
            Your Certificate of Insurance must show at least these amounts of coverage. If you are not sure whether your insurance meets these requirements, send this page to your insurance agent and they can help.
          </p>
          <div className="insurance-table-wrapper">
            <table className="insurance-table">
              <thead>
                <tr>
                  <th>Type of Coverage</th>
                  <th>Minimum Amount</th>
                </tr>
              </thead>
              <tbody>
                {INSURANCE_REQUIREMENTS.map((row) => (
                  <tr key={row.coverage}>
                    <td>{row.coverage}</td>
                    <td>{row.limit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="info-callout">
            <strong>Your insurance must also include:</strong>
            <ul>
              <li><strong>Harris Technology Services</strong> listed as an Additional Insured on your General Liability and Auto policies</li>
              <li>A <strong>Waiver of Subrogation</strong> on your Workers' Comp policy</li>
              <li>30 days' written notice before your insurance is cancelled or reduced</li>
            </ul>
          </div>
        </div>

        {/* Important Notes */}
        <div className="card">
          <h3 className="section-title">Before You Begin</h3>
          <ul className="checklist">
            <li>
              <span className="check-icon" style={{ color: 'var(--hts-warning)', background: 'rgba(217, 119, 6, 0.1)' }}>!</span>
              <span>The person filling out this form must be <strong>authorized to sign contracts</strong> on behalf of the company (usually the owner or a manager)</span>
            </li>
            <li>
              <span className="check-icon" style={{ color: 'var(--hts-warning)', background: 'rgba(217, 119, 6, 0.1)' }}>!</span>
              <span>Your COI must list <strong>"Harris Technology Services"</strong> as an Additional Insured</span>
            </li>
            <li>
              <span className="check-icon" style={{ color: 'var(--hts-warning)', background: 'rgba(217, 119, 6, 0.1)' }}>!</span>
              <span>Your W-9 must be <strong>signed and dated this year</strong></span>
            </li>
          </ul>
        </div>

        {/* Get Started — Email Verification */}
        <div className="card verify-section">
          <h3 className="section-title">Ready? Let's Get Started</h3>
          <p className="section-subtitle">
            First, enter your email address below. We will send you a 6-digit code to verify your email before you begin the application.
          </p>

          {error && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          {!codeSent ? (
            <div className="verify-form">
              <div className="form-group">
                <label>
                  Your Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourcompany.com"
                  onKeyDown={(e) => { if (e.key === 'Enter' && isValidEmail) handleSendCode(); }}
                />
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendCode}
                disabled={!isValidEmail || sending}
              >
                {sending ? (
                  <><span className="spinner" /> Sending...</>
                ) : (
                  'Send Verification Code'
                )}
              </button>
            </div>
          ) : (
            <div className="verify-form">
              <div className="alert alert-success" style={{ marginBottom: 16 }}>
                We sent a 6-digit code to <strong>{email}</strong>. Check your inbox (and spam folder) and enter the code below.
              </div>
              <div className="form-group">
                <label>
                  Enter the 6-digit code from your email <span className="required">*</span>
                </label>
                <input
                  className="verify-code-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) handleVerify(); }}
                />
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleVerify}
                disabled={code.length !== 6 || verifying}
              >
                {verifying ? (
                  <><span className="spinner" /> Checking...</>
                ) : (
                  'Verify & Start Application'
                )}
              </button>
              <p className="resend-link">
                {resendCountdown > 0 ? (
                  <>Didn't get the code? You can resend in {resendCountdown} seconds</>
                ) : (
                  <>
                    Didn't get the code?{' '}
                    <button type="button" className="link-btn" onClick={handleResend} disabled={sending}>
                      Send it again
                    </button>
                  </>
                )}
                {' \u00B7 '}
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => { setCodeSent(false); setCode(''); setError(null); }}
                >
                  Use a different email
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Help */}
        <div className="card" style={{ textAlign: 'center', padding: '24px 32px' }}>
          <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>
            <strong>Need help?</strong> Call us at{' '}
            <a href="tel:+18778772508" style={{ color: 'var(--hts-action-blue)', textDecoration: 'none', fontWeight: 600 }}>1-877-877-2508</a>{' '}
            or email{' '}
            <a href="mailto:subonboarding@hts.pro" style={{ color: 'var(--hts-action-blue)', textDecoration: 'none', fontWeight: 600 }}>subonboarding@hts.pro</a>
          </p>
        </div>
      </main>

      <footer className="app-footer">
        <p>
          &copy; {new Date().getFullYear()} Harris Technology Services &bull;{' '}
          <a href="https://hts.pro">hts.pro</a>
        </p>
      </footer>
    </div>
  );
}
